import { Component, EventEmitter, HostListener, Input, Output, ViewChild, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { ThemeService } from '../../../../services/theme.service';
import {
  currentFinancialYearRange,
  currentCalendarYearToDateRange,
  DashboardStateService,
} from '../../../../services/dashboard-state.service';
import { SidebarStateService } from '../../../../services/sidebar-state.service';
import { AvatarComponent } from '../../../../shared/UI/avatar/avatar.component';
import { combineLatest, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { RoutesEnum } from '../../../../utils/enums/routes.enum';
import { TimesheetsFilterStateService } from '../../../../services/timesheets-filter-state.service';
import { BalanceSheetItemsStateService } from '../../../../services/balance-sheet-items-state.service';
import {
  FinancialYear,
  FinancialYearService,
} from '../../../../services/apis/financial-year/financial-year.service';

interface ConfiguredFinancialYearRange {
  startDate: Date;
  endDate: Date;
}

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TooltipModule,
    DatePickerModule,
    AvatarComponent,
  ],
  templateUrl: './topbar.component.html',
})
export class TopbarComponent implements OnInit, OnDestroy {
  @ViewChild('startDatePicker') startDatePicker?: DatePicker;
  @ViewChild('endDatePicker') endDatePicker?: DatePicker;
  @ViewChild('comparisonStartDatePicker') comparisonStartDatePicker?: DatePicker;
  @ViewChild('comparisonEndDatePicker') comparisonEndDatePicker?: DatePicker;

  @Input() userName = '';
  @Input() userRole = '';
  @Input() userInitials = '';
  @Input() activeFilterCount = 0;
  @Input() showDashboardControls = true;

  @Output() exportClick = new EventEmitter<void>();

  readonly theme = inject(ThemeService);
  readonly sidebar = inject(SidebarStateService);
  private readonly dashboardState = inject(DashboardStateService);
  private readonly router = inject(Router);
  private readonly timesheetsFilterState = inject(TimesheetsFilterStateService);
  private readonly financialYearService = inject(FinancialYearService);
  readonly balanceSheetItemsState = inject(BalanceSheetItemsStateService);
  private readonly sub = new Subscription();
  private configuredThisFinancialYear: ConfiguredFinancialYearRange | null = null;
  private configuredLastFinancialYear: ConfiguredFinancialYearRange | null = null;

  startDate: Date | null = null;
  endDate: Date | null = null;
  comparisonStartDate: Date | null = null;
  comparisonEndDate: Date | null = null;
  datePresetMenuOpen = false;
  comparisonDatePresetMenuOpen = false;
  userMenuOpen = false;
  isOverviewRoute = false;
  isPlacementsRoute = false;
  isTimesheetsRoute = false;
  isBalanceSheetItemsRoute = false;
  readonly balanceSheetMaxMonth = new Date();
  placementView: 'placements' | 'live-placements' = 'placements';
  timesheetsFromDate = this.timesheetsFilterState.range.fromDate;
  timesheetsToDate = this.timesheetsFilterState.range.toDate;
  timesheetsFromDateValue = this.parseIsoDate(this.timesheetsFromDate);
  timesheetsToDateValue = this.parseIsoDate(this.timesheetsToDate);

  ngOnInit(): void {
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    this.updateOverviewRoute(this.router.url);
    this.loadConfiguredFinancialYears();
    this.sub.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          this.updateOverviewRoute(event.urlAfterRedirects);
          this.loadConfiguredFinancialYears();
        })
    );
    this.sub.add(
      this.dashboardState.comparisonDateRange$.subscribe(range => {
        this.comparisonStartDate = range?.startDate ?? null;
        this.comparisonEndDate = range?.endDate ?? null;
      })
    );

    this.sub.add(
      combineLatest([
        this.dashboardState.globalDateRange$,
        this.dashboardState.crossFilter$,
      ]).subscribe(([range, crossFilter]) => {
        if (crossFilter?.startDate && crossFilter?.endDate) {
          this.startDate = this.parseIsoDate(crossFilter.startDate);
          this.endDate = this.parseIsoDate(crossFilter.endDate);
          return;
        }

        this.startDate = range ? range.startDate : null;
        this.endDate = range ? range.endDate : null;
      })
    );
  }

  ngOnDestroy(): void {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    this.sub.unsubscribe();
  }

  onDateChange(): void {
    if (!this.startDate && !this.endDate) {
      this.dashboardState.setGlobalDateRange(null, null);
      return;
    }

    if (!this.startDate || !this.endDate) return;
    if (this.startDate > this.endDate) return;

    this.dashboardState.clearPeriodFilter();
    this.dashboardState.setGlobalDateRange(this.startDate, this.endDate);
  }

  onDateSelect(target: 'start' | 'end', value: Date | null): void {
    if (target === 'start') {
      this.startDate = value;
    } else {
      this.endDate = value;
    }
  }

  onMaskedDateInput(
    target: 'start' | 'end' | 'comparisonStart' | 'comparisonEnd',
    event: Event
  ): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    const maskedValue = this.maskDateText(input.value);
    input.value = maskedValue;
    const date = this.parseDisplayDate(maskedValue);

    // Keep the committed range while the user is editing an incomplete segment.
    // A partial DD/MM/YYYY value must not propagate null and clear both filters.
    if (!date && maskedValue.replace(/\D/g, '').length > 0) return;

    if (target === 'start') this.startDate = date;
    else if (target === 'end') this.endDate = date;
    else if (target === 'comparisonStart') this.comparisonStartDate = date;
    else this.comparisonEndDate = date;
  }

  onMaskedDateKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Backspace') return;

    const input = event.target as HTMLInputElement | null;
    if (!input || input.selectionStart === null || input.selectionEnd === null) return;

    let removeFrom = input.selectionStart;
    const removeTo = input.selectionEnd;
    if (removeFrom === removeTo) {
      removeFrom -= 1;
      while (removeFrom >= 0 && input.value[removeFrom] === '/') removeFrom -= 1;
    }
    if (removeFrom < 0) return;

    event.preventDefault();
    input.value = input.value.slice(0, removeFrom) + input.value.slice(removeTo);
    input.setSelectionRange(removeFrom, removeFrom);
  }

  onComparisonDateSelect(target: 'start' | 'end', value: Date | null): void {
    if (target === 'start') this.comparisonStartDate = value;
    else this.comparisonEndDate = value;
  }

  applyComparisonDateRange(): void {
    if (!this.startDate || !this.endDate || this.startDate > this.endDate) return;
    if (!this.comparisonStartDate || !this.comparisonEndDate || this.comparisonStartDate > this.comparisonEndDate) return;

    // A comparison always uses the date range currently visible in the primary
    // controls, even when that range has not yet been applied with its own button.
    this.dashboardState.clearPeriodFilter();
    this.dashboardState.setGlobalDateRange(this.startDate, this.endDate);
    this.dashboardState.setComparisonDateRange(this.comparisonStartDate, this.comparisonEndDate);
  }

  clearComparisonDateRange(): void {
    this.comparisonStartDate = null;
    this.comparisonEndDate = null;
    this.comparisonStartDatePicker?.clear();
    this.comparisonEndDatePicker?.clear();
    this.dashboardState.setComparisonDateRange(null, null);
  }

  clearDateRange(): void {
    const range = currentCalendarYearToDateRange();
    this.startDate = range.startDate;
    this.endDate = range.endDate;
    this.dashboardState.clearPeriodFilter();
    this.dashboardState.setSlicers({ periodPreset: 'yearToDate', month: 'all' });
    this.dashboardState.setGlobalDateRange(range.startDate, range.endDate);
  }

  searchTimesheets(): void {
    this.timesheetsFilterState.search(this.timesheetsFromDate, this.timesheetsToDate);
  }

  onTimesheetsDateChange(field: 'from' | 'to', value: Date | null): void {
    const isoValue = value ? this.formatIsoDate(value) : '';
    if (field === 'from') {
      this.timesheetsFromDateValue = value;
      this.timesheetsFromDate = isoValue;
      return;
    }
    this.timesheetsToDateValue = value;
    this.timesheetsToDate = isoValue;
  }

  resetTimesheetsDateRange(): void {
    const range = this.timesheetsFilterState.resetToCurrentYear();
    this.timesheetsFromDate = range.fromDate;
    this.timesheetsToDate = range.toDate;
    this.timesheetsFromDateValue = this.parseIsoDate(range.fromDate);
    this.timesheetsToDateValue = this.parseIsoDate(range.toDate);
  }

  clearBalanceSheetMonth(): void {
    this.balanceSheetItemsState.resetToCurrentMonth();
  }

  onBalanceSheetMonthSelect(value: Date): void {
    this.balanceSheetItemsState.setSelectedMonth(value);
  }

  toggleDatePresetMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.datePresetMenuOpen = !this.datePresetMenuOpen;
    this.comparisonDatePresetMenuOpen = false;
    if (this.datePresetMenuOpen) this.loadConfiguredFinancialYears();
  }

  toggleComparisonDatePresetMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.comparisonDatePresetMenuOpen = !this.comparisonDatePresetMenuOpen;
    this.datePresetMenuOpen = false;
    if (this.comparisonDatePresetMenuOpen) this.loadConfiguredFinancialYears();
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
  }

  @HostListener('document:click')
  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  private readonly onDocumentPointerDown = (event: PointerEvent): void => {
    if (!this.datePresetMenuOpen && !this.comparisonDatePresetMenuOpen) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-date-preset-trigger], [data-date-preset-menu]')) return;
    this.datePresetMenuOpen = false;
    this.comparisonDatePresetMenuOpen = false;
  };

  applyDatePreset(
    preset:
      | 'thisMonth'
      | 'thisFinancialYear'
      | 'lastMonth'
      | 'lastQuarter'
      | 'lastFinancialYear'
      | 'monthToDate'
      | 'quarterToDate'
      | 'yearToDate',
    target: 'primary' | 'comparison' = 'primary'
  ): void {
    const today = this.startOfDay(new Date());
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
    const thisQuarterStart = new Date(today.getFullYear(), quarterStartMonth, 1);
    const financialYear = this.configuredThisFinancialYear ?? currentFinancialYearRange(today);
    const thisFinancialYearStart = financialYear.startDate;
    const thisFinancialYearEnd = financialYear.endDate;
    const lastFinancialYear = this.configuredLastFinancialYear
      ?? this.previousFinancialYearRange(financialYear);

    const ranges: Record<typeof preset, [Date, Date]> = {
      thisMonth: [thisMonthStart, thisMonthEnd],
      thisFinancialYear: [thisFinancialYearStart, thisFinancialYearEnd],
      lastMonth: [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      ],
      lastQuarter: [
        new Date(today.getFullYear(), quarterStartMonth - 3, 1),
        new Date(today.getFullYear(), quarterStartMonth, 0),
      ],
      lastFinancialYear: [
        lastFinancialYear.startDate,
        lastFinancialYear.endDate,
      ],
      monthToDate: [thisMonthStart, today],
      quarterToDate: [thisQuarterStart, today],
      yearToDate: [thisFinancialYearStart, today],
    };

    const [start, end] = ranges[preset];
    if (target === 'comparison') {
      this.comparisonStartDate = start;
      this.comparisonEndDate = end;
      this.comparisonDatePresetMenuOpen = false;
    } else {
      this.publishDateRange(start, end);
      this.datePresetMenuOpen = false;
    }
  }

  private publishDateRange(start: Date, end: Date): void {
    this.startDate = start;
    this.endDate = end;
  }

  private loadConfiguredFinancialYears(): void {
    this.sub.add(
      this.financialYearService.getFinancialYears(false).subscribe({
        next: financialYears => this.applyConfiguredFinancialYears(financialYears),
        error: () => {
          // Keep the existing fallback ranges when company settings are unavailable.
          this.configuredThisFinancialYear = null;
          this.configuredLastFinancialYear = null;
        },
      })
    );
  }

  private applyConfiguredFinancialYears(financialYears: FinancialYear[]): void {
    const validYears = financialYears
      .map(year => ({
        year,
        range: this.parseFinancialYearRange(year),
      }))
      .filter((item): item is { year: FinancialYear; range: ConfiguredFinancialYearRange } =>
        item.range !== null
      );

    const selected = validYears.find(item => item.year.isDefault && item.year.isActive)
      ?? validYears.find(item => item.year.isDefault)
      ?? validYears.find(item => item.year.isActive)
      ?? validYears[0];

    if (!selected) {
      this.configuredThisFinancialYear = null;
      this.configuredLastFinancialYear = null;
      return;
    }

    this.configuredThisFinancialYear = selected.range;
    this.configuredLastFinancialYear = validYears
      .filter(item => item.range.endDate < selected.range.startDate)
      .sort((left, right) => right.range.endDate.getTime() - left.range.endDate.getTime())[0]?.range
      ?? this.previousFinancialYearRange(selected.range);
  }

  private parseFinancialYearRange(year: FinancialYear): ConfiguredFinancialYearRange | null {
    const startDate = this.parseIsoDate(year.startDate.slice(0, 10));
    const endDate = this.parseIsoDate(year.endDate.slice(0, 10));
    if (!startDate || !endDate || startDate > endDate) return null;
    return { startDate, endDate };
  }

  private previousFinancialYearRange(
    range: ConfiguredFinancialYearRange
  ): ConfiguredFinancialYearRange {
    return {
      startDate: this.shiftDateByYears(range.startDate, -1),
      endDate: this.shiftDateByYears(range.endDate, -1),
    };
  }

  private shiftDateByYears(date: Date, offset: number): Date {
    const targetYear = date.getFullYear() + offset;
    const month = date.getMonth();
    const day = Math.min(date.getDate(), new Date(targetYear, month + 1, 0).getDate());
    return new Date(targetYear, month, day);
  }

  private updateOverviewRoute(url: string): void {
    const path = url.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, '');
    this.isOverviewRoute = path === RoutesEnum.Dashboard;
    this.isPlacementsRoute = path === RoutesEnum.Placements;
    this.isTimesheetsRoute = path === RoutesEnum.Timesheets;
    this.isBalanceSheetItemsRoute = path === RoutesEnum.BalanceSheetItemName;
    const view = this.router.parseUrl(url).queryParamMap.get('view');
    this.placementView = view === 'live' ? 'live-placements' : 'placements';
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private parseIsoDate(value: string): Date | null {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private formatIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private maskDateText(value: string): string {
    if (value.includes('/')) {
      const segments = value.split('/');
      const day = (segments[0] ?? '').replace(/\D/g, '').slice(0, 2);
      const month = (segments[1] ?? '').replace(/\D/g, '').slice(0, 2);
      const year = (segments[2] ?? '').replace(/\D/g, '').slice(0, 4);
      return [day, month, year].join('/');
    }

    let digits = value.replace(/\D/g, '').slice(0, 8);

    if (digits.length >= 2 && Number(digits.slice(0, 2)) > 31) {
      digits = digits.slice(0, 1);
    }

    if (digits.length >= 4 && Number(digits.slice(2, 4)) > 12) {
      digits = digits.slice(0, 3);
    }

    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    if (digits.length <= 2) return digits.length === 2 ? `${day}/` : day;
    if (digits.length <= 4) return digits.length === 4 ? `${day}/${month}/` : `${day}/${month}`;
    return `${day}/${month}/${year}`;
  }

  private parseDisplayDate(value: string): Date | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      ? date
      : null;
  }

}
